<template>
  <div>
    <Head title="Auditoría del Sistema" />

    <!-- Top Header -->
    <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
      <div class="flex items-center gap-3">
        <button 
          class="p-2.5 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm"
          title="Regresar"
          @click="back"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <div class="flex items-center gap-2">
            <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">Auditoría del Sistema</h1>
            <span class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
              Logs
            </span>
          </div>
          <p class="text-sm text-gray-500 mt-0.5">
            Registro cronológico de actividades, accesos y operaciones de los usuarios
          </p>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex items-center gap-2">
        <a 
          :href="exportUrl"
          class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition shadow-sm"
          title="Exportar registros filtrados a CSV"
        >
          <i class="fa fa-file-csv text-emerald-600"></i>
          <span class="hidden sm:inline">Exportar CSV</span>
        </a>

        <button 
          class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition shadow-sm"
          title="Actualizar registros"
          @click="refresh"
        >
          <i class="fa fa-sync-alt" :class="{'fa-spin': isRefreshing}"></i>
          <span class="hidden sm:inline">Actualizar</span>
        </button>
      </div>
    </div>

    <!-- KPI Summary Cards -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <!-- Total Logs -->
      <div class="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4">
        <div class="w-12 h-12 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 text-xl shrink-0">
          <i class="fa fa-clipboard-list"></i>
        </div>
        <div class="min-w-0">
          <p class="text-xs font-medium text-gray-500 uppercase tracking-wider truncate">Total Registros</p>
          <p class="text-2xl font-bold text-gray-900 mt-0.5">{{ stats.total_logs ?? 0 }}</p>
        </div>
      </div>

      <!-- Hoy -->
      <div class="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4">
        <div class="w-12 h-12 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 text-xl shrink-0">
          <i class="fa fa-calendar-day"></i>
        </div>
        <div class="min-w-0">
          <p class="text-xs font-medium text-gray-500 uppercase tracking-wider truncate">Actividad Hoy</p>
          <p class="text-2xl font-bold text-emerald-700 mt-0.5">{{ stats.today_logs ?? 0 }}</p>
        </div>
      </div>

      <!-- Usuarios Activos -->
      <div class="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4">
        <div class="w-12 h-12 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 text-xl shrink-0">
          <i class="fa fa-users"></i>
        </div>
        <div class="min-w-0">
          <p class="text-xs font-medium text-gray-500 uppercase tracking-wider truncate">Usuarios Registrados</p>
          <p class="text-2xl font-bold text-gray-900 mt-0.5">{{ stats.active_users ?? 0 }}</p>
        </div>
      </div>

      <!-- Acción Principal -->
      <div class="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4">
        <div class="w-12 h-12 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600 text-xl shrink-0">
          <i class="fa fa-bolt"></i>
        </div>
        <div class="min-w-0">
          <p class="text-xs font-medium text-gray-500 uppercase tracking-wider truncate">Acción Más Frecuente</p>
          <p class="text-lg font-bold text-gray-900 mt-0.5 truncate" :title="stats.top_category">
            {{ stats.top_category ?? 'N/A' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Filter Control Panel -->
    <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6">
      <div class="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b border-gray-100">
        <div class="flex items-center gap-2">
          <i class="fa fa-filter text-indigo-600"></i>
          <span class="font-bold text-gray-800 text-sm">Filtros de Búsqueda</span>
          <span 
            v-if="activeFiltersCount > 0"
            class="px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded-full text-xs font-bold"
          >
            {{ activeFiltersCount }} activo{{ activeFiltersCount > 1 ? 's' : '' }}
          </span>
        </div>

        <!-- Quick Date Range Presets -->
        <div class="flex flex-wrap items-center gap-1.5 text-xs">
          <span class="text-gray-400 mr-1 hidden sm:inline">Rango rápido:</span>
          <button 
            type="button"
            class="px-2.5 py-1 rounded border transition-colors"
            :class="isPresetActive('today') ? 'bg-indigo-600 text-white border-indigo-600 font-semibold' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'"
            @click="setPreset('today')"
          >
            Hoy
          </button>
          <button 
            type="button"
            class="px-2.5 py-1 rounded border transition-colors"
            :class="isPresetActive('yesterday') ? 'bg-indigo-600 text-white border-indigo-600 font-semibold' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'"
            @click="setPreset('yesterday')"
          >
            Ayer
          </button>
          <button 
            type="button"
            class="px-2.5 py-1 rounded border transition-colors"
            :class="isPresetActive('last7') ? 'bg-indigo-600 text-white border-indigo-600 font-semibold' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'"
            @click="setPreset('last7')"
          >
            Últimos 7 días
          </button>
          <button 
            type="button"
            class="px-2.5 py-1 rounded border transition-colors"
            :class="isPresetActive('month') ? 'bg-indigo-600 text-white border-indigo-600 font-semibold' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'"
            @click="setPreset('month')"
          >
            Este Mes
          </button>
          <button 
            v-if="activeFiltersCount > 0"
            type="button"
            class="px-2.5 py-1 rounded border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 font-medium ml-1 transition"
            @click="reset"
          >
            <i class="fa fa-times mr-1"></i>Limpiar Todo
          </button>
        </div>
      </div>

      <!-- Filter Form Grid -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Text Search -->
        <div class="lg:col-span-1">
          <label class="block text-xs font-semibold text-gray-600 mb-1">Buscar Término</label>
          <div class="relative">
            <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
              <i class="fa fa-search text-xs"></i>
            </span>
            <input 
              v-model="form.search"
              type="text" 
              placeholder="Descripción, usuario..." 
              class="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        <!-- User Filter -->
        <div>
          <label class="block text-xs font-semibold text-gray-600 mb-1">Filtrar por Usuario</label>
          <select 
            v-model="form.user_id"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
          >
            <option :value="null">Todos los usuarios</option>
            <option v-for="u in users" :key="u.id" :value="u.id">
              {{ u.name }} ({{ u.role }})
            </option>
          </select>
        </div>

        <!-- Action / Category Filter -->
        <div>
          <label class="block text-xs font-semibold text-gray-600 mb-1">Filtrar por Acción</label>
          <select 
            v-model="form.category_id"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
          >
            <option :value="null">Todas las acciones</option>
            <option v-for="cat in categories" :key="cat.id" :value="cat.id">
              {{ cat.name }}
            </option>
          </select>
        </div>

        <!-- Date Range: From / To -->
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1">Fecha Desde</label>
            <input 
              v-model="form.date_start"
              type="date" 
              class="w-full px-2.5 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1">Fecha Hasta</label>
            <input 
              v-model="form.date_end"
              type="date" 
              class="w-full px-2.5 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Audit Data Table -->
    <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
      <div class="overflow-x-auto">
        <table class="w-full text-left text-sm border-collapse">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wider">
              <th class="py-3.5 px-4 sm:px-6">Fecha y Hora</th>
              <th class="py-3.5 px-4 sm:px-6">Usuario Responsable</th>
              <th class="py-3.5 px-4 sm:px-6">Acción / Categoría</th>
              <th class="py-3.5 px-4 sm:px-6">Descripción del Evento</th>
              <th class="py-3.5 px-4 sm:px-6 text-right">Detalles</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="log in logs.data" 
              :key="log.id"
              class="hover:bg-gray-50/80 transition-colors"
            >
              <!-- Fecha -->
              <td class="py-3.5 px-4 sm:px-6 whitespace-nowrap">
                <div class="font-medium text-gray-900">{{ log.date_formatted }}</div>
                <div class="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                  <i class="far fa-clock text-[10px]"></i>
                  <span>{{ log.time_ago }}</span>
                </div>
              </td>

              <!-- Usuario -->
              <td class="py-3.5 px-4 sm:px-6 whitespace-nowrap">
                <div class="flex items-center gap-2.5">
                  <div class="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold text-xs flex items-center justify-center shrink-0 shadow-sm">
                    {{ log.user.initials }}
                  </div>
                  <div>
                    <div class="font-semibold text-gray-900">{{ log.user.name }}</div>
                    <div class="text-xs text-gray-500 flex items-center gap-1">
                      <span>{{ log.user.email || 'Sin correo' }}</span>
                      <span class="text-gray-300">•</span>
                      <span class="capitalize text-gray-500 font-medium">{{ log.user.role }}</span>
                    </div>
                  </div>
                </div>
              </td>

              <!-- Acción / Categoría -->
              <td class="py-3.5 px-4 sm:px-6 whitespace-nowrap">
                <span 
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border"
                  :class="log.category.badge_class"
                >
                  <i :class="['fas', log.category.icon, 'text-[11px]']"></i>
                  <span>{{ log.category.name }}</span>
                </span>
              </td>

              <!-- Descripción -->
              <td class="py-3.5 px-4 sm:px-6">
                <div class="text-gray-800 line-clamp-2 max-w-md font-normal leading-relaxed">
                  {{ log.description }}
                </div>
              </td>

              <!-- Detalles / Modal -->
              <td class="py-3.5 px-4 sm:px-6 text-right whitespace-nowrap">
                <button 
                  class="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition"
                  title="Ver detalle del registro"
                  @click="openDetailModal(log)"
                >
                  <i class="fa fa-eye"></i>
                </button>
              </td>
            </tr>

            <!-- Empty State -->
            <tr v-if="logs.data.length === 0">
              <td colspan="5" class="py-12 px-6 text-center">
                <div class="max-w-sm mx-auto flex flex-col items-center">
                  <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 text-2xl mb-3">
                    <i class="fa fa-search"></i>
                  </div>
                  <h3 class="text-base font-bold text-gray-800">No se encontraron registros</h3>
                  <p class="text-xs text-gray-500 mt-1 mb-4">
                    No existen eventos de auditoría que coincidan con los filtros aplicados.
                  </p>
                  <button 
                    v-if="activeFiltersCount > 0"
                    type="button"
                    class="btn-blue text-xs py-2 px-4"
                    @click="reset"
                  >
                    Restablecer filtros
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Table Footer / Count info -->
      <div v-if="logs.data.length > 0" class="px-6 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between">
        <span>Mostrando registros {{ logs.from || 1 }} - {{ logs.to || logs.data.length }} de {{ logs.total || logs.data.length }}</span>
        <span>Filtrado automático en tiempo real</span>
      </div>
    </div>

    <!-- Pagination -->
    <pagination class="mt-4" :links="logs.links" />

    <!-- Detail Inspection Modal -->
    <div 
      v-if="selectedLog"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-xs transition-opacity"
      @click.self="selectedLog = null"
    >
      <div class="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100 transform transition-all animate-in fade-in zoom-in-95 duration-150">
        <!-- Modal Header -->
        <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/70">
          <div class="flex items-center gap-2">
            <span 
              class="w-8 h-8 rounded-lg flex items-center justify-center text-sm border"
              :class="selectedLog.category.badge_class"
            >
              <i :class="['fas', selectedLog.category.icon]"></i>
            </span>
            <div>
              <h3 class="text-base font-bold text-gray-900">Registro de Auditoría #{{ selectedLog.id }}</h3>
              <p class="text-xs text-gray-500">{{ selectedLog.category.name }}</p>
            </div>
          </div>
          <button 
            class="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition"
            @click="selectedLog = null"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="p-6 space-y-4 text-sm">
          <!-- Description Card -->
          <div>
            <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Descripción de la Operación
            </label>
            <div class="p-3.5 bg-gray-50 rounded-xl border border-gray-200 text-gray-900 font-medium leading-relaxed">
              {{ selectedLog.description }}
            </div>
          </div>

          <!-- Metadata Grid -->
          <div class="grid grid-cols-2 gap-4">
            <!-- User info -->
            <div class="p-3 bg-gray-50/70 rounded-xl border border-gray-100">
              <span class="block text-xs font-medium text-gray-500 mb-1">Usuario</span>
              <div class="font-bold text-gray-900">{{ selectedLog.user.name }}</div>
              <div class="text-xs text-gray-500 mt-0.5">{{ selectedLog.user.email }}</div>
              <span class="inline-block mt-1 px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-[10px] font-semibold uppercase tracking-wider">
                Rol: {{ selectedLog.user.role }}
              </span>
            </div>

            <!-- Date & Time info -->
            <div class="p-3 bg-gray-50/70 rounded-xl border border-gray-100">
              <span class="block text-xs font-medium text-gray-500 mb-1">Fecha y Hora</span>
              <div class="font-bold text-gray-900">{{ selectedLog.date_formatted }}</div>
              <div class="text-xs text-indigo-600 font-medium mt-0.5">{{ selectedLog.time_ago }}</div>
              <span class="block text-[10px] text-gray-400 font-mono mt-1 truncate" :title="selectedLog.created_at">
                {{ selectedLog.created_at }}
              </span>
            </div>
          </div>

          <!-- Action Category info -->
          <div class="p-3 bg-gray-50/70 rounded-xl border border-gray-100 flex items-center justify-between">
            <div>
              <span class="block text-xs font-medium text-gray-500">Categoría del Evento</span>
              <span class="font-bold text-gray-800 text-sm">{{ selectedLog.category.name }}</span>
            </div>
            <span 
              class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border"
              :class="selectedLog.category.badge_class"
            >
              <i :class="['fas', selectedLog.category.icon]"></i>
              <span>{{ selectedLog.category.name }}</span>
            </span>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
          <button 
            type="button" 
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition shadow-sm"
            @click="selectedLog = null"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import Pagination from '@/Shared/Pagination'

export default {
  components: {
    Head,
    Link,
    Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    logs: Object,
    users: Array,
    categories: Array,
    stats: Object,
  },
  data() {
    return {
      form: {
        search: this.filters.search || null,
        user_id: this.filters.user_id ? Number(this.filters.user_id) : null,
        category_id: this.filters.category_id ? Number(this.filters.category_id) : null,
        date_start: this.filters.date_start || null,
        date_end: this.filters.date_end || null,
      },
      selectedLog: null,
      isRefreshing: false,
    }
  },
  computed: {
    activeFiltersCount() {
      let count = 0
      if (this.form.search) count++
      if (this.form.user_id !== null && this.form.user_id !== undefined && this.form.user_id !== '') count++
      if (this.form.category_id !== null && this.form.category_id !== undefined && this.form.category_id !== '') count++
      if (this.form.date_start) count++
      if (this.form.date_end) count++
      return count
    },
    exportUrl() {
      const params = new URLSearchParams()
      if (this.form.search) params.append('search', this.form.search)
      if (this.form.user_id) params.append('user_id', this.form.user_id)
      if (this.form.category_id) params.append('category_id', this.form.category_id)
      if (this.form.date_start) params.append('date_start', this.form.date_start)
      if (this.form.date_end) params.append('date_end', this.form.date_end)
      const qs = params.toString()
      return '/logs/export' + (qs ? '?' + qs : '')
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        const cleaned = pickBy(this.form, (val) => val !== null && val !== undefined && val !== '')
        this.$inertia.get('/logs', cleaned, { 
          preserveState: true, 
          preserveScroll: true,
          replace: true 
        })
      }, 200),
    },
  },
  methods: {
    back() {
      window.history.back()
    },
    refresh() {
      this.isRefreshing = true
      const cleaned = pickBy(this.form, (val) => val !== null && val !== undefined && val !== '')
      this.$inertia.get('/logs', cleaned, {
        preserveState: true,
        preserveScroll: true,
        onFinish: () => {
          this.isRefreshing = false
        },
      })
    },
    reset() {
      this.form = {
        search: null,
        user_id: null,
        category_id: null,
        date_start: null,
        date_end: null,
      }
    },
    openDetailModal(log) {
      this.selectedLog = log
    },
    formatDateYMD(date) {
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    setPreset(type) {
      const now = new Date()
      const todayStr = this.formatDateYMD(now)

      if (type === 'today') {
        this.form.date_start = todayStr
        this.form.date_end = todayStr
      } else if (type === 'yesterday') {
        const y = new Date()
        y.setDate(now.getDate() - 1)
        const yStr = this.formatDateYMD(y)
        this.form.date_start = yStr
        this.form.date_end = yStr
      } else if (type === 'last7') {
        const past = new Date()
        past.setDate(now.getDate() - 7)
        this.form.date_start = this.formatDateYMD(past)
        this.form.date_end = todayStr
      } else if (type === 'month') {
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
        this.form.date_start = this.formatDateYMD(firstDay)
        this.form.date_end = todayStr
      }
    },
    isPresetActive(type) {
      const now = new Date()
      const todayStr = this.formatDateYMD(now)

      if (type === 'today') {
        return this.form.date_start === todayStr && this.form.date_end === todayStr
      }
      if (type === 'yesterday') {
        const y = new Date()
        y.setDate(now.getDate() - 1)
        const yStr = this.formatDateYMD(y)
        return this.form.date_start === yStr && this.form.date_end === yStr
      }
      if (type === 'last7') {
        const past = new Date()
        past.setDate(now.getDate() - 7)
        return this.form.date_start === this.formatDateYMD(past) && this.form.date_end === todayStr
      }
      if (type === 'month') {
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
        return this.form.date_start === this.formatDateYMD(firstDay) && this.form.date_end === todayStr
      }
      return false
    },
  },
}
</script>
